import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosRelatives } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

/* =====================================================
 * HELPERS
 * ===================================================== */
const normalizeError = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data ||
  err?.message ||
  fallback;

/* =====================================================
 * FETCH RELATIVES BY USER
 * ===================================================== */
export const fetchRelativesByUserId = createAsyncThunk(
  "relatives/fetchByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        return rejectWithValue("User ID is required");
      }

      const { data } = await axiosRelatives.get(
        ENDPOINTS.RELATIVES.BY_USER(userId)
      );

      // 🔐 Toujours retourner un tableau
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return rejectWithValue(
        normalizeError(err, "Erreur lors du chargement des proches")
      );
    }
  }
);

/* =====================================================
 * CREATE RELATIVE
 * ===================================================== */
export const createRelative = createAsyncThunk(
  "relatives/create",
  async (payload, { rejectWithValue }) => {
    try {
      if (!payload?.userId) {
        return rejectWithValue("User ID is missing");
      }

      const { data } = await axiosRelatives.post(
        ENDPOINTS.RELATIVES.BASE,
        payload
      );

      /**
       * data peut être :
       * - un objet Relative (compte existant)
       * - null (invitation email envoyée)
       */
      return data ?? null;
    } catch (err) {
      return rejectWithValue(
        normalizeError(err, "Erreur lors de la création du proche")
      );
    }
  }
);

/* =====================================================
 * UPDATE RELATIVE
 * ===================================================== */
export const updateRelative = createAsyncThunk(
  "relatives/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue("Relative ID is required");
      }

      const { data } = await axiosRelatives.patch(
        ENDPOINTS.RELATIVES.BY_ID(id),
        payload
      );

      return data;
    } catch (err) {
      return rejectWithValue(
        normalizeError(err, "Erreur lors de la mise à jour")
      );
    }
  }
);

/* =====================================================
 * ACCEPT RELATIVE
 * ===================================================== */
export const acceptRelative = createAsyncThunk(
  "relatives/accept",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue("Relative ID is required");
      }

      const { data } = await axiosRelatives.patch(
        ENDPOINTS.RELATIVES.ACCEPT(id)
      );

      return data;
    } catch (err) {
      return rejectWithValue(
        normalizeError(err, "Erreur lors de l’acceptation")
      );
    }
  }
);

/* =====================================================
 * DELETE RELATIVE
 * ===================================================== */
export const deleteRelative = createAsyncThunk(
  "relatives/delete",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue("Relative ID is required");
      }

      await axiosRelatives.delete(
        ENDPOINTS.RELATIVES.BY_ID(id)
      );

      return id;
    } catch (err) {
      return rejectWithValue(
        normalizeError(err, "Erreur lors de la suppression")
      );
    }
  }
);